import React, { useState } from 'react';
import DeleteScheduleLineGroupIcon from '@mui/icons-material/EventBusy';
import { Box, Button, DialogContentText, Tooltip } from '@mui/material';

import { closeCurrentPopup, Dialog } from '@paragon/popup-state';

import { deleteScheduleGroup, editScheduleGroup } from '../../../actions/scheduleGroupActions';
import { getBundlesByScheduleGroupGuid } from '../../../selectors/getBundlesByScheduleGroupGuid';
import { getStatusesByScheduleGroupGuid } from '../../../selectors/scheduleGroups';
import { RootState } from '../../../store';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { ScheduleGroupProperties } from './ScheduleGroupProperties';

export interface EditScheduleGroupDialogProps {
  readonly scheduleGroupGuid: string;
}

export function EditScheduleGroupDialog(props: EditScheduleGroupDialogProps) {
  const { scheduleGroupGuid } = props;
  const dispatch = useAppDispatch();
  const scheduleGroup = useAppSelector((state: RootState) => state.scheduleGroups)[scheduleGroupGuid];
  const bundlesByScheduleGroupGuid = useAppSelector(getBundlesByScheduleGroupGuid);
  const statusesByScheduleGroupGuid = useAppSelector(getStatusesByScheduleGroupGuid);

  const [scheduleGroupName, setScheduleGroupName] = useState<string | null>(scheduleGroup.name);
  const [scheduleGroupNameError, setScheduleGroupNameError] = useState<boolean>(false);
  const [targetDate, setTargetDate] = useState<string | null>(scheduleGroup.targetDate);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);

  const isStatusOtherThanUnscheduled =
    statusesByScheduleGroupGuid[scheduleGroupGuid].filter((status) => status !== 'Unscheduled').length > 0;
  const areScheduleLineGroupsForBundlesInScheduleGroup = bundlesByScheduleGroupGuid[scheduleGroupGuid].some(
    (bundle) => bundle.scheduleLineGroupGuid !== null,
  );
  const areTruckloadsForBundlesInScheduleGroup = bundlesByScheduleGroupGuid[scheduleGroupGuid].some(
    (bundle) => bundle.truckloadGuid !== null,
  );
  const deletingDisabled =
    isStatusOtherThanUnscheduled ||
    areScheduleLineGroupsForBundlesInScheduleGroup ||
    areTruckloadsForBundlesInScheduleGroup;

  function edit() {
    if (!scheduleGroupName) return;

    dispatch(editScheduleGroup(scheduleGroup.guid, scheduleGroupName, targetDate));
    dispatch(closeCurrentPopup());
  }

  return (
    <Dialog
      title="Edit Schedule Group"
      open
      submitText="Edit"
      onSubmit={edit}
      onCancel={() => void dispatch(closeCurrentPopup())}
      contentSx={{ width: '400px', height: '250px' }}
      submitDisabled={scheduleGroupName === null}
      enterKeySubmits
    >
      <Box sx={{ mb: 2 }}>
        <ScheduleGroupProperties
          scheduleGroupName={scheduleGroupName}
          setScheduleGroupName={(newScheduleGroupName) => {
            setScheduleGroupName(newScheduleGroupName);
            if (newScheduleGroupName === '') setScheduleGroupNameError(true);
          }}
          scheduleGroupNameError={scheduleGroupNameError}
          targetDate={targetDate}
          setTargetDate={setTargetDate}
        />
      </Box>
      <Tooltip
        disableInteractive
        title={
          isStatusOtherThanUnscheduled
            ? 'Some bundles in this schedule group have a status other than "Unscheduled" so it cannot be deleted'
            : areScheduleLineGroupsForBundlesInScheduleGroup
              ? 'Some bundles in this schedule group are part of a schedule line group. Delete the schedule line group(s) first.'
              : areTruckloadsForBundlesInScheduleGroup
                ? 'Some bundles in this schedule group are part of a truckload. Delete them from the truckload(s) first.'
                : 'Delete this schedule group - all its bundles will return to the backlog'
        }
      >
        <span>
          <Button
            variant="outlined"
            onClick={() => void setConfirmDeleteOpen(true)}
            startIcon={<DeleteScheduleLineGroupIcon sx={{ color: 'red' }} />}
            disabled={deletingDisabled}
          >
            Delete
          </Button>
        </span>
      </Tooltip>
      <Dialog
        open={confirmDeleteOpen}
        variant="design"
        title="Confirm Delete"
        onSubmit={() => {
          dispatch(deleteScheduleGroup(scheduleGroupGuid));
          setConfirmDeleteOpen(false);
          dispatch(closeCurrentPopup());
        }}
        onCancel={() => void setConfirmDeleteOpen(false)}
      >
        <DialogContentText>
          Delete Schedule Group &quot;{scheduleGroup.name}&quot;? All bundles associated with this Schedule Group will
          no longer be associated with it.
        </DialogContentText>
      </Dialog>
    </Dialog>
  );
}
